import type { AttributionStatus, CreatorStatus, PayoutStatus } from './types';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export interface CreatorProgramTab {
  id: number;
  slug: string;
  label: string;
}

/**
 * Tab config follows the Menu feature's pattern (`components/Menu/Constant.js`):
 * the active tab lives in the URL (`/creator-program/:tab`), not in `useState`.
 * Components are wired in `index.tsx` rather than here, to keep this module free
 * of component imports.
 */
export const TAB_CONFIG: CreatorProgramTab[] = [
  { id: 0, slug: 'creators', label: 'Creators' },
  { id: 1, slug: 'terms', label: 'Commission Terms' },
  { id: 2, slug: 'leaderboard', label: 'Performance Leaderboard' },
  { id: 3, slug: 'attributions', label: 'Attribution Ledger' },
  { id: 4, slug: 'payouts', label: 'Payouts' },
];

export const DEFAULT_TAB_SLUG = 'creators';

export const isKnownTabSlug = (slug?: string): boolean =>
  TAB_CONFIG.some((tab) => tab.slug === slug);

export const BASE_ROUTE = '/creator-program';

export const creatorDetailRoute = (id: string): string =>
  `${BASE_ROUTE}/creator/${id}`;

export const tabRoute = (slug: string): string => `${BASE_ROUTE}/${slug}`;

// ---------------------------------------------------------------------------
// React Query keys
// ---------------------------------------------------------------------------

export const QUERY_KEYS = {
  CREATORS: 'creator-program:creators',
  CREATOR: 'creator-program:creator',
  CREATOR_PERFORMANCE: 'creator-program:creator-performance',
  LEADERBOARD: 'creator-program:leaderboard',
  ATTRIBUTIONS: 'creator-program:attributions',
  PAYOUTS: 'creator-program:payouts',
  PAYOUT: 'creator-program:payout',
} as const;

/** Rows per page on every paginated table here. */
export const DEFAULT_PAGE_SIZE = 20;

/** Max the list endpoint allows, so a creator picker covers most merchants in one call. */
export const CREATOR_PICKER_LIMIT = 100;

export const STALE_TIME_MS = 60 * 1000;
export const PERFORMANCE_STALE_TIME_MS = 0;

// ---------------------------------------------------------------------------
// Status colour maps (antd Tag colours), following `Orders/constants.ts`
// ---------------------------------------------------------------------------

export const CREATOR_STATUS_COLORS: Record<CreatorStatus, string> = {
  active: 'success',
  paused: 'warning',
  terminated: 'error',
};

export const ATTRIBUTION_STATUS_COLORS: Record<AttributionStatus, string> = {
  pending: 'gold',
  payable: 'blue',
  paid: 'green',
  reversed: 'red',
};

export const PAYOUT_STATUS_COLORS: Record<PayoutStatus, string> = {
  draft: 'default',
  approved: 'blue',
  paid: 'green',
  cancelled: 'red',
};

export const getStatusColor = (
  status: string | undefined,
  map: Record<string, string>
): string => {
  const key = status?.toLowerCase();
  return (key && map[key]) || 'default';
};

export const CREATOR_STATUS_OPTIONS: { value: CreatorStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'terminated', label: 'Terminated' },
];

export const ATTRIBUTION_STATUS_OPTIONS: { value: AttributionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'payable', label: 'Payable' },
  { value: 'paid', label: 'Paid' },
  { value: 'reversed', label: 'Reversed' },
];

export const PAYOUT_STATUS_OPTIONS: { value: PayoutStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ---------------------------------------------------------------------------
// Field constraints (docs §2.1, §2.4, §2.5) — mirrored client-side to fail fast
// ---------------------------------------------------------------------------

export const FIELD_LIMITS = {
  NAME_MAX: 160,
  HANDLE_MAX: 100,
  CODE_PREFIX_MAX: 24,
  PASSWORD_MIN: 8,
  REFERENCE_MAX: 255,
  PERCENT_ALLOWANCE_MAX: 100,
  DEFAULT_MAX_ACTIVE_CODES: 5,
} as const;

export const CODE_PREFIX_PATTERN = /^[a-zA-Z0-9]+$/;

// ---------------------------------------------------------------------------
// UI copy
// ---------------------------------------------------------------------------

export const PAGE_TITLE = 'Creator Program';
export const PAGE_SUBTITLE =
  'Manage creators, commission terms, attributed sales and payouts';

export const TAB_DESCRIPTIONS: Record<string, string> = {
  creators: 'Add creators and manage their status and code prefix',
  terms: 'Set versioned commission terms for a creator',
  leaderboard: 'Creator performance ranked by net creator earnings',
  attributions: 'Order-level ledger of every commission-earning sale and refund',
  payouts: 'Draft, approve and record creator payouts',
};

/**
 * Docs §3.2 gotcha: `earnings.pending`/`payable` only reflect activity inside the
 * requested window, so they are not the creator's live balance. This caption must
 * accompany every render of those figures.
 */
export const WINDOWED_EARNINGS_CAPTION = 'Within selected date range';
export const WINDOWED_EARNINGS_TOOLTIP =
  'These figures cover only activity inside the selected date range. They are not the ' +
  "creator's current outstanding balance — a payable balance from before the range " +
  'will not appear here.';

/** Docs §1.3/§2.4: no update endpoint exists for these fields. */
export const EDIT_LIMITED_FIELDS_NOTICE =
  'Only status and code prefix can be changed. Name, handle, email and password have ' +
  'no update endpoint yet.';

/** Docs §1.4: terms are immutable and versioned — never framed as an edit. */
export const TERMS_VERSIONING_NOTICE =
  'Saving creates a new terms version and immediately supersedes the current one. ' +
  'Existing codes and orders keep the terms they were created under.';

/** Docs §1: code-level management is only available in the creator portal. */
export const CODE_MANAGEMENT_NOTICE =
  'Per-code performance is read-only here. Pausing or revoking an individual code is ' +
  "only available from the creator's own portal.";

export const EMPTY_STATES = {
  CREATORS: 'No creators yet',
  CREATORS_DESCRIPTION: 'Add your first creator to start the program.',
  LEADERBOARD: 'No performance data for this range',
  ATTRIBUTIONS: 'No attributed orders for these filters',
  PAYOUTS: 'No payouts yet',
  BY_STORE: 'No store activity in this range',
  BY_CODE: 'No code activity in this range',
  LINKS: 'No link activity in this range',
  TERMS: 'No commission terms set yet',
} as const;

export const METRIC_LABELS = {
  ORDERS: 'Orders',
  GROSS_SUBTOTAL: 'Gross Subtotal',
  COMMISSION_BASE: 'Commission Base',
  NET_CUSTOMER_DISCOUNT: 'Customer Discount (net)',
  NET_CREATOR_EARNINGS: 'Creator Earnings (net)',
  TOTAL_COST: 'Total Cost',
  COST_PERCENT_OF_BASE: 'Cost % of Base',
  AOV: 'Average Order Value',
  NEW_CUSTOMERS: 'New',
  RETURNING_CUSTOMERS: 'Returning',
  UNKNOWN_CUSTOMERS: 'Unknown',
  CUSTOMER_MIX: 'New vs Returning Customers',
  REFUND_SUBTOTAL: 'Refunded Subtotal',
  REFUND_COUNT: 'Refunds',
  EARNINGS_PENDING: 'Pending',
  EARNINGS_PAYABLE: 'Payable',
  EARNINGS_PAID: 'Paid',
  CLICKS: 'Clicks',
  UNIQUE_CLICKS: 'Unique Clicks',
  CONVERSION: 'Conversion',
} as const;

export const EXPORT_FILENAME_PREFIX = 'creator-payouts';
